import faker from 'faker';
import {
	AlertTriangle,
	BarChart3,
	Clock,
	FileText,
	FolderOpen,
} from 'lucide-react';
import { Toaster } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table';

faker.seed(55);

type ActivityEvent =
	| 'Alert Created'
	| 'Case Assigned'
	| 'Alert Disposed'
	| 'STR Filed'
	| 'Case Escalated'
	| 'Alert Closed'
	| 'Case Reopened';

type ActivityRow = {
	id: string;
	time: string;
	event: ActivityEvent;
	entity: string;
	user: string;
};

const ACTIVITY_EVENTS: ActivityEvent[] = [
	'Alert Created',
	'Case Assigned',
	'Alert Disposed',
	'STR Filed',
	'Case Escalated',
	'Alert Closed',
	'Case Reopened',
];

const RECENT_ACTIVITY: ActivityRow[] = Array.from({ length: 10 }, (_, i) => ({
	id: faker.datatype.uuid(),
	time: faker.date.recent(1).toISOString(),
	event: ACTIVITY_EVENTS[i % ACTIVITY_EVENTS.length],
	entity: faker.name.findName(),
	user: faker.name.findName(),
})).sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime());

function formatTime(iso: string): string {
	return new Date(iso).toLocaleTimeString('en-US', {
		hour: '2-digit',
		minute: '2-digit',
		hour12: false,
	});
}

function eventBadge(event: ActivityEvent) {
	switch (event) {
		case 'Alert Created':
			return (
				<Badge className="border border-blue-200 bg-blue-50 text-blue-700 text-xs hover:bg-blue-50">
					{event}
				</Badge>
			);
		case 'STR Filed':
			return (
				<Badge className="border border-orange-200 bg-orange-50 text-orange-700 text-xs hover:bg-orange-50">
					{event}
				</Badge>
			);
		case 'Case Escalated':
			return (
				<Badge className="border border-red-200 bg-red-50 text-red-700 text-xs hover:bg-red-50">
					{event}
				</Badge>
			);
		case 'Alert Disposed':
		case 'Alert Closed':
			return (
				<Badge className="border border-gray-200 bg-gray-50 text-gray-600 text-xs hover:bg-gray-50">
					{event}
				</Badge>
			);
		case 'Case Assigned':
			return (
				<Badge className="border border-purple-200 bg-purple-50 text-purple-700 text-xs hover:bg-purple-50">
					{event}
				</Badge>
			);
		case 'Case Reopened':
			return (
				<Badge className="border border-yellow-200 bg-yellow-50 text-yellow-700 text-xs hover:bg-yellow-50">
					{event}
				</Badge>
			);
	}
}

export default function AmlDashboardPage() {
	return (
		<div className="min-h-screen bg-background">
			<Toaster richColors position="top-right" />
			<div className="border-b border-border bg-white px-6 py-4">
				<h1 className="text-xl font-semibold text-[#0A0A0A]">
					Risk Radar Dashboard
				</h1>
				<p className="mt-0.5 text-sm text-[#737373]">
					Real-time overview of AML alerts, cases, and compliance activity
				</p>
			</div>

			<div className="p-6 flex flex-col gap-6">
				<div className="grid grid-cols-4 gap-4">
					<Card>
						<CardContent className="pt-4">
							<div className="flex items-start justify-between">
								<div>
									<p className="text-xs text-[#737373]">Open Alerts</p>
									<p className="mt-1 text-3xl font-bold text-blue-600">127</p>
								</div>
								<AlertTriangle className="h-5 w-5 text-blue-400" />
							</div>
						</CardContent>
					</Card>
					<Card>
						<CardContent className="pt-4">
							<div className="flex items-start justify-between">
								<div>
									<p className="text-xs text-[#737373]">Open Cases</p>
									<p className="mt-1 text-3xl font-bold text-purple-600">43</p>
								</div>
								<FolderOpen className="h-5 w-5 text-purple-400" />
							</div>
						</CardContent>
					</Card>
					<Card>
						<CardContent className="pt-4">
							<div className="flex items-start justify-between">
								<div>
									<p className="text-xs text-[#737373]">STRs Filed</p>
									<p className="mt-1 text-3xl font-bold text-orange-600">18</p>
								</div>
								<FileText className="h-5 w-5 text-orange-400" />
							</div>
						</CardContent>
					</Card>
					<Card>
						<CardContent className="pt-4">
							<div className="flex items-start justify-between">
								<div>
									<p className="text-xs text-[#737373]">Avg Resolution Time</p>
									<p className="mt-1 text-3xl font-bold text-green-600">3.2</p>
									<p className="text-xs text-[#737373]">days</p>
								</div>
								<Clock className="h-5 w-5 text-green-400" />
							</div>
						</CardContent>
					</Card>
				</div>

				<div className="grid grid-cols-2 gap-4">
					<Card>
						<CardHeader className="pb-2">
							<CardTitle className="text-sm font-semibold text-[#0a0a0a] flex items-center gap-2">
								<BarChart3 className="h-4 w-4" />
								Alert Summary
							</CardTitle>
						</CardHeader>
						<CardContent>
							<Table>
								<TableHeader>
									<TableRow className="bg-[#fafafa]">
										<TableHead className="text-xs font-semibold text-[#737373]">
											Module
										</TableHead>
										<TableHead className="text-xs font-semibold text-[#737373] text-right">
											Open
										</TableHead>
										<TableHead className="text-xs font-semibold text-[#737373] text-right">
											In Review
										</TableHead>
										<TableHead className="text-xs font-semibold text-[#737373] text-right">
											Closed
										</TableHead>
										<TableHead className="text-xs font-semibold text-[#737373] text-right">
											Total
										</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									<TableRow>
										<TableCell className="text-sm text-[#0a0a0a]">
											Name Screening
										</TableCell>
										<TableCell className="text-sm text-[#525252] text-right">
											52
										</TableCell>
										<TableCell className="text-sm text-[#525252] text-right">
											24
										</TableCell>
										<TableCell className="text-sm text-[#525252] text-right">
											13
										</TableCell>
										<TableCell className="text-sm font-medium text-[#0a0a0a] text-right">
											89
										</TableCell>
									</TableRow>
									<TableRow>
										<TableCell className="text-sm text-[#0a0a0a]">
											Transaction Monitoring
										</TableCell>
										<TableCell className="text-sm text-[#525252] text-right">
											18
										</TableCell>
										<TableCell className="text-sm text-[#525252] text-right">
											12
										</TableCell>
										<TableCell className="text-sm text-[#525252] text-right">
											8
										</TableCell>
										<TableCell className="text-sm font-medium text-[#0a0a0a] text-right">
											38
										</TableCell>
									</TableRow>
									<TableRow className="bg-[#fafafa]">
										<TableCell className="text-sm font-semibold text-[#0a0a0a]">
											Total
										</TableCell>
										<TableCell className="text-sm font-semibold text-blue-600 text-right">
											70
										</TableCell>
										<TableCell className="text-sm font-semibold text-[#0a0a0a] text-right">
											36
										</TableCell>
										<TableCell className="text-sm font-semibold text-[#0a0a0a] text-right">
											21
										</TableCell>
										<TableCell className="text-sm font-semibold text-[#0a0a0a] text-right">
											127
										</TableCell>
									</TableRow>
								</TableBody>
							</Table>
						</CardContent>
					</Card>

					<Card>
						<CardHeader className="pb-2">
							<CardTitle className="text-sm font-semibold text-[#0a0a0a] flex items-center gap-2">
								<FolderOpen className="h-4 w-4" />
								Case Summary
							</CardTitle>
						</CardHeader>
						<CardContent>
							<Table>
								<TableHeader>
									<TableRow className="bg-[#fafafa]">
										<TableHead className="text-xs font-semibold text-[#737373]">
											Status
										</TableHead>
										<TableHead className="text-xs font-semibold text-[#737373] text-right">
											Count
										</TableHead>
										<TableHead className="text-xs font-semibold text-[#737373] text-right">
											%
										</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{[
										{ status: 'Open', count: 15, color: 'text-blue-600' },
										{
											status: 'In Review',
											count: 12,
											color: 'text-yellow-600',
										},
										{ status: 'Escalated', count: 8, color: 'text-red-600' },
										{ status: 'Closed', count: 43, color: 'text-gray-600' },
										{ status: 'Discarded', count: 7, color: 'text-gray-400' },
									].map(({ status, count, color }) => (
										<TableRow key={status}>
											<TableCell className="text-sm text-[#0a0a0a]">
												{status}
											</TableCell>
											<TableCell
												className={`text-sm font-medium text-right ${color}`}
											>
												{count}
											</TableCell>
											<TableCell className="text-sm text-[#737373] text-right">
												{Math.round((count / 85) * 100)}%
											</TableCell>
										</TableRow>
									))}
									<TableRow className="bg-[#fafafa]">
										<TableCell className="text-sm font-semibold text-[#0a0a0a]">
											Total
										</TableCell>
										<TableCell className="text-sm font-semibold text-[#0a0a0a] text-right">
											85
										</TableCell>
										<TableCell className="text-sm text-[#737373] text-right">
											100%
										</TableCell>
									</TableRow>
								</TableBody>
							</Table>
						</CardContent>
					</Card>
				</div>

				<Card>
					<CardHeader className="pb-2">
						<CardTitle className="text-sm font-semibold text-[#0a0a0a]">
							Recent Activity
						</CardTitle>
					</CardHeader>
					<CardContent className="p-0">
						<Table>
							<TableHeader>
								<TableRow className="bg-[#fafafa]">
									<TableHead className="text-xs font-semibold text-[#737373]">
										Time
									</TableHead>
									<TableHead className="text-xs font-semibold text-[#737373]">
										Event
									</TableHead>
									<TableHead className="text-xs font-semibold text-[#737373]">
										Entity
									</TableHead>
									<TableHead className="text-xs font-semibold text-[#737373]">
										User
									</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{RECENT_ACTIVITY.map((row) => (
									<TableRow key={row.id}>
										<TableCell className="text-sm text-[#737373] font-mono">
											{formatTime(row.time)}
										</TableCell>
										<TableCell>{eventBadge(row.event)}</TableCell>
										<TableCell className="text-sm text-[#0a0a0a]">
											{row.entity}
										</TableCell>
										<TableCell className="text-sm text-[#525252]">
											{row.user}
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</CardContent>
				</Card>
			</div>
		</div>
	);
}
